// FinalConsentRecording — the end-of-interview consent recording phase
// (build-order step 10, Slice C). The subject reads the single LegacyLens
// consent statement aloud; the recording is uploaded to
// interview-final-consent-upload, which transcribes it, scores it against the
// canonical statement server-side, and returns a QA verdict. This component
// drives the read and reacts to that verdict.
//
// Flow: play intro -> show the LegacyLens statement -> manual Record/Stop (one
// take) -> review (Submit / Re-record) -> on Submit, upload behind a
// non-cancelable "Checking…" state -> branch on the server response:
//   * accepted (data.accepted === true, incl. the idempotent double-submit):
//     brief "We got that" then onComplete().
//   * hard failure (supabase-js `error`; status/body read from error.context):
//     onError() — technical faults (auth, oversized, STT/R2/DB, wrong state).
//     NEVER mic-denied: mic-denied only originates from the recorder itself.
//   * soft QA reject (HTTP 200, data.accepted === false): a re-read. reason is
//     "duration" | "word_match"; data.transcript is LOG-ONLY (server side) and
//     is never stored in state nor shown to the subject.
//
// Retry policy (consecutive SERVER rejects only): MAX_ATTEMPTS reads. A
// voluntary Re-record tap does NOT consume an attempt — only an accepted:false
// response does. The re-prompt before the final read says it is the last
// attempt. After the last reject the subject sees a gentle terminal screen and
// Exits; the interview stays RESUMABLE (the backend flips interviews.status ->
// complete only on an accept), so they can return via the magic link later.
//
// Provenance is server-side: interview-final-consent-upload re-fetches the
// active LegacyLens row and records its id, so the upload sends only `audio` +
// `duration_ms` — no statement id is transmitted. consentStatements.legacylens
// supplies the displayed `.text` ([name] is substituted server-side for scoring
// only; the on-screen text is shown verbatim). `.id` is intentionally not sent.
//
// Preserves the QuestionFlow-derived primitives: cbRef/alive insulation against
// parent re-renders, recordWithSilenceDetection with silenceMs === maxDurationMs
// (only the manual Stop button or the safety cap finalizes — no silence-stop),
// and the unmount cleanup (alive=false -> stopAllAudio -> abort the recorder).
// All TTS goes through playPrompt/stopAllAudio.

import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import {
  MicPermissionDeniedError,
  recordWithSilenceDetection,
  type RecordResult,
} from "@/lib/audioRecorder";
import { playPrompt, stopAllAudio } from "@/lib/gateAudio";
import type { ConsentStatementsPayload } from "@/lib/interviewTypes";
import { RecordingPulse, SpeakerPulse } from "@/components/interview/_visuals";

// Generous safety cap so a slow reader is never clipped; the manual Stop button
// is the intended terminator. silenceMs is pinned EQUAL to the cap so the
// fixed-RMS silence branch can never finalize before the cap — only Stop
// (stoppedReason "manual") or the cap (stoppedReason "max_duration") ends it.
const STATEMENT_MAX_DURATION_MS = 180_000;
// Pin opus to speech-grade 64 kbps for a deterministic, compact upload (matches
// the server's 2 MB ceiling: 180s @ 64 kbps ≈ 1.44 MB).
const STATEMENT_BITRATE_BPS = 64_000;

// Consecutive server QA rejects allowed before the terminal pause screen. A
// voluntary Re-record does NOT count; only an accepted:false response does.
const MAX_ATTEMPTS = 3;

const INTRO_PROMPT =
  "You have completed the interview process. You'll now read one short consent statement aloud. When you're ready, tap Record, read the statement, and press Stop.";
const REVIEW_PROMPT =
  "Are you ready to submit, or would you like to re-record?";
const RETRY_PROMPT =
  "I couldn't quite make out the statement. Let's try once more — tap Record and read it again.";
const LAST_TRY_PROMPT =
  "Let's try one more time. This is the last attempt — tap Record and read the statement slowly and clearly.";
const ACCEPTED_PROMPT = "We got that. Thank you.";
const EXHAUSTED_PROMPT =
  "Let's pause for now. You can come back to this anytime.";

// Subject-safe, generic on-screen copy for a server QA reject. The transcript is
// LOG-ONLY (server side) and the score is never surfaced — only this generic
// guidance is shown. Returns null when there's no prior reject to explain.
const rejectMessage = (reason: string | undefined): string | null => {
  if (reason === "word_match")
    return "We couldn't quite make out the words — please read the statement as shown.";
  if (reason === "duration")
    return "That recording was too short — please record the whole statement before tapping Stop.";
  return null;
};

type Phase =
  | "intro" // playing the intro prompt before the read
  | "ready_to_record" // statement shown, waiting for the Record tap
  | "recording" // recording in progress, Stop button shown
  | "review" // recorded; Submit / Re-record buttons
  | "submitting" // upload + QA in flight; non-cancelable
  | "accepted" // server accepted; brief confirmation, then onComplete
  | "exhausted"; // MAX_ATTEMPTS server rejects; gentle terminal screen

type UploadOutcome =
  | { kind: "fail"; status?: number; error: string }
  | { kind: "accepted" }
  | { kind: "rejected"; reason: string };

// Upload the consent recording to interview-final-consent-upload. Modeled on
// QuestionFlow.tsx's uploadAnswer: supabase.functions.invoke with an explicit
// Bearer session token + a multipart FormData. Maps the three server outcomes:
// a supabase-js `error` (with the Response on error.context) is a hard fail; an
// HTTP-200 body carries accepted:true | accepted:false (reason).
const uploadFinalConsent = async (
  sessionToken: string,
  blob: Blob,
  mimeType: string,
  durationMs: number,
): Promise<UploadOutcome> => {
  const ext = mimeType.includes("mp4") || mimeType.includes("aac") ? "m4a" : "webm";
  const form = new FormData();
  form.append("audio", new File([blob], `final-legacylens.${ext}`, { type: mimeType }));
  form.append("duration_ms", String(Math.round(durationMs)));

  const { data, error } = await supabase.functions.invoke<{
    ok?: boolean;
    accepted?: boolean;
    reason?: string;
  }>(
    "interview-final-consent-upload",
    { body: form, headers: { Authorization: `Bearer ${sessionToken}` } },
  );

  if (error) {
    const ctx = (error as unknown as { context?: Response }).context;
    let status: number | undefined;
    let message = "request failed";
    if (ctx && typeof ctx.status === "number") {
      status = ctx.status;
      try {
        const body = (await ctx.clone().json()) as { error?: string };
        message = body?.error ?? message;
      } catch {
        // leave default
      }
    }
    return { kind: "fail", status, error: message };
  }
  if (data?.accepted === true) return { kind: "accepted" };
  if (data?.accepted === false) return { kind: "rejected", reason: data.reason ?? "word_match" };
  return { kind: "fail", error: "malformed response" };
};

export type FinalConsentRecordingProps = {
  audioContext: AudioContext;
  consentStatements: ConsentStatementsPayload;
  sessionToken: string;
  onComplete: () => void;
  onError: (message: string) => void;
  onMicDenied: () => void;
  onExit: () => void;
};

const FinalConsentRecording = ({
  audioContext,
  consentStatements,
  sessionToken,
  onComplete,
  onError,
  onMicDenied,
  onExit,
}: FinalConsentRecordingProps) => {
  const [phase, setPhase] = useState<Phase>("intro");
  // Display-only retry feedback, derived from the loop's `rejects` counter (which
  // remains the source of truth). n = the read the subject is ABOUT to do
  // (rejects + 1); reason = the most recent server reject. null on the first read.
  const [attemptInfo, setAttemptInfo] = useState<{ n: number; reason?: string } | null>(null);

  // Insulate the async sequence from parent re-renders (callback identities
  // change every render but the sequence must not restart) — same pattern as
  // QuestionFlow.
  const cbRef = useRef({ onComplete, onError, onMicDenied, onExit });
  cbRef.current = { onComplete, onError, onMicDenied, onExit };

  // AbortController for the in-flight recording, aborted by Stop / unmount.
  const recordStopRef = useRef<AbortController | null>(null);
  // Resolver for the "waiting for the Record tap" gate.
  const recordTapRef = useRef<(() => void) | null>(null);
  // Resolver for the Submit / Re-record decision.
  const decisionRef = useRef<((d: "submit" | "re-record") => void) | null>(null);

  useEffect(() => {
    let alive = true;

    // Resolves when the subject taps Record.
    function waitForRecordTap(): Promise<void> {
      return new Promise<void>((resolve) => {
        recordTapRef.current = resolve;
      });
    }

    // Resolves with the Submit / Re-record choice.
    function waitForDecision(): Promise<"submit" | "re-record"> {
      return new Promise<"submit" | "re-record">((resolve) => {
        decisionRef.current = resolve;
      });
    }

    // One take: show the statement, wait for Record, record until Stop (or the
    // safety cap), then review. A voluntary Re-record loops for a fresh take
    // (no attempt consumed). Returns the take to submit, or null if unmounted.
    async function captureTake(): Promise<RecordResult | null> {
      while (true) {
        setPhase("ready_to_record");
        await waitForRecordTap();
        if (!alive) return null;

        setPhase("recording");
        const stop = new AbortController();
        recordStopRef.current = stop;
        const rec = await recordWithSilenceDetection({
          maxDurationMs: STATEMENT_MAX_DURATION_MS,
          silenceMs: STATEMENT_MAX_DURATION_MS, // >= max: silence never finalizes
          audioBitsPerSecond: STATEMENT_BITRATE_BPS,
          audioContext,
          stopSignal: stop.signal,
        });
        recordStopRef.current = null;
        if (!alive) return null;

        setPhase("review");
        await playPrompt(REVIEW_PROMPT);
        if (!alive) return null;
        const decision = await waitForDecision();
        if (!alive) return null;
        if (decision === "submit") return rec;
        // "re-record": voluntary, no attempt consumed — loop for another take.
      }
    }

    async function runFlow(): Promise<void> {
      try {
        setPhase("intro");
        await playPrompt(INTRO_PROMPT);
        if (!alive) return;

        let rejects = 0;
        while (true) {
          const rec = await captureTake();
          if (!alive || !rec) return;

          setPhase("submitting");
          const outcome = await uploadFinalConsent(
            sessionToken,
            rec.blob,
            rec.mimeType,
            rec.durationMs,
          );
          if (!alive) return;

          if (outcome.kind === "fail") {
            // Hard failure (4xx/5xx) — technical, not a misread. Never mic-denied.
            cbRef.current.onError(outcome.error);
            return;
          }
          if (outcome.kind === "accepted") {
            setPhase("accepted");
            await playPrompt(ACCEPTED_PROMPT);
            if (!alive) return;
            cbRef.current.onComplete();
            return;
          }

          // Soft QA reject (outcome.kind === "rejected"). The transcript is
          // server-side LOG-ONLY — nothing about it is stored or shown here.
          rejects += 1;
          if (rejects >= MAX_ATTEMPTS) {
            setPhase("exhausted");
            await playPrompt(EXHAUSTED_PROMPT);
            return; // resumable: backend flips to complete only on accept.
          }
          // Display-only: surface the upcoming attempt number + the reject reason.
          setAttemptInfo({ n: rejects + 1, reason: outcome.reason });
          const lastTry = rejects === MAX_ATTEMPTS - 1;
          await playPrompt(lastTry ? LAST_TRY_PROMPT : RETRY_PROMPT);
          if (!alive) return;
          // loop -> captureTake again (another attempt)
        }
      } catch (err) {
        if (!alive) return;
        if (err instanceof MicPermissionDeniedError) {
          cbRef.current.onMicDenied();
          return;
        }
        const msg = err instanceof Error ? err.message : "unknown error";
        cbRef.current.onError(`Recording failed: ${msg}`);
      }
    }

    void runFlow();

    return () => {
      // alive=false FIRST so a resolving recorder drops its partial (the
      // `if (!alive) return` after each await fires), then stop prompt audio and
      // release the mic if a recording is in flight.
      alive = false;
      stopAllAudio();
      recordStopRef.current?.abort();
    };
  }, [audioContext]);

  // Single statement. `.id` is intentionally unused — the server re-fetches the
  // active legacylens row for provenance; only `.text` is displayed.
  const statement = consentStatements.legacylens;
  const reasonText = rejectMessage(attemptInfo?.reason);
  const isLastTry = attemptInfo?.n === MAX_ATTEMPTS;

  const onExitClick = () => {
    stopAllAudio();
    cbRef.current.onExit();
  };

  const onRecordClick = () => {
    const resume = recordTapRef.current;
    recordTapRef.current = null;
    resume?.();
  };

  const onStopClick = () => {
    recordStopRef.current?.abort();
  };

  const onDecision = (d: "submit" | "re-record") => {
    const resolve = decisionRef.current;
    decisionRef.current = null;
    resolve?.(d);
  };

  const ExitButton = () => (
    <Button
      size="lg"
      variant="ghost"
      onClick={onExitClick}
      className="text-base text-muted-foreground hover:text-foreground"
    >
      Exit
    </Button>
  );

  return (
    <div className="text-center" aria-live="polite">
      <div className="mb-6 text-sm uppercase tracking-wide text-muted-foreground">
        Final consent
        {attemptInfo && attemptInfo.n > 1 ? ` · Try ${attemptInfo.n} of ${MAX_ATTEMPTS}` : ""}
      </div>

      {phase === "recording" ? <RecordingPulse /> : <SpeakerPulse />}

      {phase === "intro" ? (
        <>
          <p className="mt-6 text-2xl font-display text-foreground">Listen carefully</p>
          <p className="mt-2 text-base text-muted-foreground">
            We'll show you the words to read in a moment.
          </p>
          <div className="mt-4">
            <ExitButton />
          </div>
        </>
      ) : null}

      {phase === "ready_to_record" ? (
        <>
          <p className="mt-6 text-2xl font-display text-foreground">When you're ready</p>
          <p className="mt-4 text-base text-foreground max-w-md mx-auto whitespace-pre-line">
            {statement.text}
          </p>
          <p className="mt-4 text-base text-muted-foreground">
            Tap Record, read the statement aloud, then tap Stop.
          </p>
          {reasonText ? (
            <p className="mt-2 text-sm text-muted-foreground">{reasonText}</p>
          ) : null}
          {isLastTry ? (
            <p className="mt-2 text-xs text-muted-foreground">
              This is the last attempt — please read the full statement clearly.
            </p>
          ) : null}
          <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Button
              size="lg"
              onClick={onRecordClick}
              className="text-xl px-10 py-7 h-auto rounded-2xl"
            >
              Record
            </Button>
            <ExitButton />
          </div>
        </>
      ) : null}

      {phase === "recording" ? (
        <>
          <p className="mt-6 text-2xl font-display text-foreground">Recording</p>
          <p className="mt-4 text-base text-foreground max-w-md mx-auto whitespace-pre-line">
            {statement.text}
          </p>
          <p className="mt-4 text-base text-muted-foreground">
            Read the statement aloud, then tap Stop when you're finished.
          </p>
          {reasonText ? (
            <p className="mt-2 text-sm text-muted-foreground">{reasonText}</p>
          ) : null}
          <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Button
              size="lg"
              onClick={onStopClick}
              className="text-xl px-10 py-7 h-auto rounded-2xl"
            >
              Stop
            </Button>
            <ExitButton />
          </div>
        </>
      ) : null}

      {phase === "review" ? (
        <>
          <p className="mt-6 text-2xl font-display text-foreground">Ready to submit?</p>
          <p className="mt-2 text-base text-muted-foreground">
            Submit your recording, or re-record it from the start.
          </p>
          <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Button
              size="lg"
              onClick={() => onDecision("submit")}
              className="text-xl px-10 py-7 h-auto rounded-2xl"
            >
              Submit
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => onDecision("re-record")}
              className="text-xl px-10 py-7 h-auto rounded-2xl"
            >
              Re-record
            </Button>
            <ExitButton />
          </div>
        </>
      ) : null}

      {phase === "submitting" ? (
        <>
          <p className="mt-6 text-2xl font-display text-foreground">Checking your recording…</p>
          <p className="mt-2 text-base text-muted-foreground">
            One moment — this won't take long.
          </p>
        </>
      ) : null}

      {phase === "accepted" ? (
        <>
          <p className="mt-6 text-2xl font-display text-foreground">We got that</p>
          <p className="mt-2 text-base text-muted-foreground">Thank you.</p>
        </>
      ) : null}

      {phase === "exhausted" ? (
        <>
          <p className="mt-6 text-2xl font-display text-foreground">Let's pause for now</p>
          <p className="mt-2 text-base text-muted-foreground">
            You can come back to this anytime.
          </p>
          <div className="mt-4 flex justify-center">
            <Button
              size="lg"
              onClick={onExitClick}
              className="text-xl px-10 py-7 h-auto rounded-2xl"
            >
              Done for now
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default FinalConsentRecording;
